import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';
import api from '../../utils/api';
import { useAuth } from '../../context/AuthContext';
import { errorText } from '../../utils/errorText';
import { can } from '../../utils/permissions';
import { displayName } from '../../utils/userLabel';
import { input } from '../shared/styles';

/*
 * The sign-in log (routes/login_audit.py, migration 0074): every attempt to
 * sign in, with a password or with Google, that succeeded or failed -- for
 * those who administer accounts (login_audit:read: collection managers and
 * system admins). A deep link from anyone else is told so and asks the
 * server nothing, as /admin/issues does.
 *
 * At the top, the last day's failures and the addresses they came from (a
 * press filters by one). Then tabs by outcome with their counts, the method,
 * the reason (with how many of each the other filters leave), a search over
 * the address typed, the account and the IP, and a date range; all of it in
 * the address bar, so a view can be sent to a colleague. Newest first, 50 at
 * a time. The log tells a wrong password from an unknown address; the sign-in
 * screen never does.
 */
export const OUTCOMES = ['all', 'failure', 'success'];
const METHODS = ['all', 'password', 'google'];
const PAGE = 50;

const when = (iso, lang) => {
  if (!iso) return '';
  try {
    return new Date(iso).toLocaleString(lang === 'he' ? 'he-IL' : 'en-GB',
      { dateStyle: 'medium', timeStyle: 'medium' });
  } catch (e) {
    return iso;
  }
};

/** "Chrome · Android" from a user agent, or '' -- the full text is the cell's title. */
export function browserOf(ua) {
  if (!ua) return '';
  const browser = /Edg\//.test(ua) ? 'Edge' : /OPR\//.test(ua) ? 'Opera' : /Firefox\//.test(ua) ? 'Firefox'
    : /Chrome\//.test(ua) ? 'Chrome' : /Safari\//.test(ua) ? 'Safari' : '';
  const system = /Android/.test(ua) ? 'Android' : /iPhone|iPad/.test(ua) ? 'iOS' : /Windows/.test(ua) ? 'Windows'
    : /Mac OS X/.test(ua) ? 'macOS' : /Linux/.test(ua) ? 'Linux' : '';
  return [browser, system].filter(Boolean).join(' · ') || ua.slice(0, 40);
}

/** A local date (yyyy-mm-dd) as the ISO moment its day begins; `next` the day after. */
export function dayStart(value, next = false) {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(value || '')) return null;
  const [y, m, d] = value.split('-').map(Number);
  return new Date(y, m - 1, d + (next ? 1 : 0)).toISOString();
}

function Outcome({ outcome, t }) {
  const ok = outcome === 'success';
  return (
    <span data-testid="login-outcome" data-outcome={outcome}
      style={{ display: 'inline-block', padding: '1px 8px', borderRadius: 10, fontSize: 12, whiteSpace: 'nowrap',
        background: ok ? 'var(--c-success-soft, var(--c-bg-muted))' : 'var(--c-danger-soft, var(--c-bg-muted))',
        color: ok ? 'var(--c-success-fg)' : 'var(--c-danger-fg)' }}>
      <span aria-hidden="true">{ok ? '✓' : '✗'}</span> {t(`outcome.${outcome}`)}
    </span>
  );
}

export default function LoginAudit() {
  const { t, i18n } = useTranslation('loginAudit');
  const { user } = useAuth();
  const allowed = can(user, 'login_audit:read');
  const [params, setParams] = useSearchParams();
  const outcome = OUTCOMES.includes(params.get('outcome')) ? params.get('outcome') : 'all';
  const method = METHODS.includes(params.get('method')) ? params.get('method') : 'all';
  const reason = params.get('reason') || '';
  const q = params.get('q') || '';
  const from = params.get('from') || '';
  const to = params.get('to') || '';
  const [typed, setTyped] = useState(q);
  const [offset, setOffset] = useState(0);
  const [data, setData] = useState(null);
  const [error, setError] = useState('');
  const lang = i18n?.language;

  const setParam = useCallback((changes) => {
    const next = new URLSearchParams(params);
    Object.entries(changes).forEach(([k, v]) => {
      if (v === null || v === undefined || v === '' || v === 'all') next.delete(k);
      else next.set(k, v);
    });
    setOffset(0);
    setParams(next, { replace: true });
  }, [params, setParams]);

  // The search box waits for a pause in typing.
  useEffect(() => {
    if (typed.trim() === q) return undefined;
    const timer = setTimeout(() => setParam({ q: typed.trim() }), 300);
    return () => clearTimeout(timer);
  }, [typed, q, setParam]);

  const load = useCallback(async () => {
    setError('');
    try {
      const { data: body } = await api.get('/admin/login-events', {
        params: {
          outcome, method, limit: PAGE, offset,
          ...(reason ? { reason } : {}), ...(q ? { q } : {}),
          ...(dayStart(from) ? { from: dayStart(from) } : {}), ...(dayStart(to, true) ? { to: dayStart(to, true) } : {}),
        },
      });
      setData(body);
    } catch (e) {
      setError(errorText(e));
    }
  }, [outcome, method, reason, q, from, to, offset]);
  useEffect(() => { if (allowed) load(); }, [load, allowed]);

  if (!allowed) {
    return <p style={{ color: 'var(--c-text-muted-fg)' }} data-testid="logins-restricted">{t('accessRestricted')}</p>;
  }

  const counts = data?.counts || {};
  const items = data?.items || [];
  const lastDay = data?.last_day;
  const reasonLabel = r => t(`reasons.${r}`, { defaultValue: r });

  return (
    <div>
      <h2>{t('pageTitle')}</h2>
      <p style={{ color: 'var(--c-text-muted-fg)', marginTop: 0 }}>{t('pageIntro', { days: data?.keep_days ?? 180 })}</p>

      {lastDay && (
        <section data-testid="logins-last-day" aria-label={t('lastDay.title')}
          style={{ border: '1px solid var(--c-border)', borderRadius: 10, padding: '10px 12px', marginBottom: 12,
                   background: lastDay.failures ? 'var(--c-warning-soft, var(--c-surface))' : 'var(--c-surface)' }}>
          <strong style={{ fontSize: 14 }}>{t('lastDay.title')}</strong>
          <p style={{ margin: '4px 0', fontSize: 14 }} data-testid="logins-last-day-summary">
            {t('lastDay.summary', { failures: lastDay.failures, addresses: lastDay.addresses })}
          </p>
          {lastDay.top?.length > 0 && (
            <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', alignItems: 'center', fontSize: 13 }}>
              <span>{t('lastDay.top')}</span>
              {lastDay.top.map(a => (
                <button key={a.ip} type="button" data-testid="logins-top-ip" title={t('tip.filterIp')}
                  onClick={() => { setTyped(a.ip); setParam({ q: a.ip }); }}
                  style={{ border: '1px solid var(--c-border-strong)', borderRadius: 999, padding: '2px 10px', cursor: 'pointer',
                           background: 'var(--c-surface)', color: 'var(--c-text-fg)', fontSize: 13 }}>
                  <bdi dir="ltr">{a.ip}</bdi> · {a.failures}
                </button>
              ))}
            </div>
          )}
        </section>
      )}

      <div role="tablist" aria-label={t('tabsLabel')} style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 12 }}>
        {OUTCOMES.map(name => (
          <button key={name} type="button" role="tab" aria-selected={outcome === name} data-testid={`logins-tab-${name}`}
            onClick={() => setParam({ outcome: name })}
            style={{ border: '1px solid var(--c-border)', borderRadius: 999, padding: '6px 14px', fontSize: 14, cursor: 'pointer',
              minHeight: 40, background: outcome === name ? 'var(--c-primary)' : 'var(--c-surface)',
              color: outcome === name ? 'var(--c-on-accent)' : 'var(--c-text-fg)' }}>
            {t(`tab.${name}`)} <span aria-hidden="true">({counts[name] ?? 0})</span>
          </button>
        ))}
      </div>

      <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'flex-end', marginBottom: 12 }}>
        <label style={fieldStyle}>
          <span>{t('filter.search')}</span>
          <input type="search" value={typed} onChange={e => setTyped(e.target.value)} placeholder={t('filter.searchHint')}
            data-testid="logins-search" style={{ ...input, minHeight: 36, width: 240, maxWidth: '100%', boxSizing: 'border-box' }} />
        </label>
        <label style={fieldStyle}>
          <span>{t('filter.method')}</span>
          <select value={method} onChange={e => setParam({ method: e.target.value })} data-testid="logins-method"
            style={{ ...input, minHeight: 36 }}>
            {METHODS.map(m => <option key={m} value={m}>{t(`method.${m}`)}</option>)}
          </select>
        </label>
        <label style={fieldStyle}>
          <span>{t('filter.reason')}</span>
          <select value={reason} onChange={e => setParam({ reason: e.target.value })} data-testid="logins-reason"
            style={{ ...input, minHeight: 36, maxWidth: 260 }}>
            <option value="">{t('filter.anyReason')}</option>
            {(data?.reasons || []).map(r => (
              <option key={r.reason} value={r.reason}>{reasonLabel(r.reason)} ({r.count})</option>
            ))}
            {reason && !(data?.reasons || []).some(r => r.reason === reason) && (
              <option value={reason}>{reasonLabel(reason)}</option>
            )}
          </select>
        </label>
        <label style={fieldStyle}>
          <span>{t('filter.from')}</span>
          <input type="date" value={from} max={to || undefined} onChange={e => setParam({ from: e.target.value })}
            data-testid="logins-from" style={{ ...input, minHeight: 36 }} />
        </label>
        <label style={fieldStyle}>
          <span>{t('filter.to')}</span>
          <input type="date" value={to} min={from || undefined} onChange={e => setParam({ to: e.target.value })}
            data-testid="logins-to" style={{ ...input, minHeight: 36 }} />
        </label>
        {(q || method !== 'all' || reason || from || to || outcome !== 'all') && (
          <button type="button" data-testid="logins-clear" title={t('tip.clear')}
            onClick={() => { setTyped(''); setOffset(0); setParams(new URLSearchParams(), { replace: true }); }}
            style={{ border: '1px solid var(--c-border-strong)', borderRadius: 6, padding: '6px 12px', minHeight: 36,
                     background: 'var(--c-surface)', color: 'var(--c-text-fg)', cursor: 'pointer' }}>
            {t('filter.clear')}
          </button>
        )}
      </div>

      {error && <p role="alert" style={{ color: 'var(--c-danger-fg)' }}>{error}</p>}
      {data && items.length === 0 && <p data-testid="logins-empty" style={{ color: 'var(--c-text-muted-fg)' }}>{t('empty')}</p>}
      {items.length > 0 && (
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 14 }} data-testid="logins-table">
            <thead>
              <tr style={{ borderBottom: '1px solid var(--c-border)' }}>
                {['when', 'outcome', 'method', 'address', 'account', 'reason', 'ip', 'browser'].map(c => (
                  <th key={c} scope="col" style={{ textAlign: 'start', padding: '6px 8px', whiteSpace: 'nowrap' }}>{t(`col.${c}`)}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {items.map(e => (
                <tr key={e.event_id} data-testid="logins-row" data-outcome={e.outcome}
                  style={{ borderBottom: '1px solid var(--c-border)' }}>
                  <td style={cell} title={e.occurred_at}>{when(e.occurred_at, lang)}</td>
                  <td style={cell}><Outcome outcome={e.outcome} t={t} /></td>
                  <td style={cell}>{t(`method.${e.method}`)}</td>
                  <td style={{ ...cell, overflowWrap: 'anywhere', whiteSpace: 'normal' }}>
                    {e.identifier ? <bdi dir="ltr">{e.identifier}</bdi>
                      : <span style={{ color: 'var(--c-text-muted-fg)' }} title={t('tip.notAnAddress')}>{t('notAnAddress')}</span>}
                  </td>
                  <td style={{ ...cell, whiteSpace: 'normal' }}>
                    {e.user_id ? displayName(e.full_name || e.username, t) : <span style={{ color: 'var(--c-text-muted-fg)' }}>—</span>}
                  </td>
                  <td style={{ ...cell, whiteSpace: 'normal' }} data-testid="login-reason" data-reason={e.reason || ''}>
                    {e.reason ? reasonLabel(e.reason) : ''}
                  </td>
                  <td style={cell}>
                    {e.ip ? (
                      <button type="button" title={t('tip.filterIp')} onClick={() => { setTyped(e.ip); setParam({ q: e.ip }); }}
                        style={{ background: 'none', border: 'none', padding: 0, cursor: 'pointer', color: 'var(--c-info-fg)', font: 'inherit' }}>
                        <bdi dir="ltr">{e.ip}</bdi>
                      </button>
                    ) : '—'}
                  </td>
                  <td style={cell} title={e.user_agent || ''}>{browserOf(e.user_agent)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {data && data.total > PAGE && (
        <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginTop: 12 }}>
          <button type="button" data-testid="logins-prev" disabled={offset === 0}
            onClick={() => setOffset(Math.max(0, offset - PAGE))}>{t('prev')}</button>
          <span style={{ fontSize: 13 }}>{t('pageOf', { from: offset + 1, to: Math.min(offset + PAGE, data.total), total: data.total })}</span>
          <button type="button" data-testid="logins-next" disabled={offset + PAGE >= data.total}
            onClick={() => setOffset(offset + PAGE)}>{t('next')}</button>
        </div>
      )}
    </div>
  );
}

const fieldStyle = { display: 'flex', flexDirection: 'column', gap: 4, fontSize: 13 };
const cell = { padding: '6px 8px', whiteSpace: 'nowrap', verticalAlign: 'top' };
